/**
 * The entity route modules' document answer (web plan W4.4):
 * src/routes/{brand,shop,store,promotion}.tsx.
 *
 * The loader reads the SEO server's decision for the URL through the load
 * context (`seo.entity(type)`, server/framework.mjs): the same answer the root
 * loader reads (one `decide` call per request), plus the entity's detail-query
 * data, so the server-rendered body describes exactly what the head does.
 *
 * - 301 → a thrown `redirect(location, 301)` (never `redirect()`'s default
 *   302), Location as the server built it (query kept);
 * - 404 / 410 → a thrown `data(null, status)`: the route's ErrorBoundary,
 *   with the root's "{Type} not found" head (`entityMeta`);
 * - 503 → a thrown `data(null, 503)` with `Retry-After`;
 * - 200 → `{ head, entity, preload? }` with W1.3's Cache-Control (`preload`:
 *   the secondary sections' answers, @/hooks/routePreload);
 * - no load context (`react-router dev`, tests) → `{ head: null, entity:
 *   null }`: the page loads its data in the browser, as the SPA did.
 *
 * `headers` keeps the thrown answer's headers (`Retry-After`, `no-store`).
 * `entityMeta` is the route's own head when it has one, else the root's
 * answer (see its comment).
 *
 * Pure module: no `window`, no React.
 */
import {
  data,
  redirect,
  type AppLoadContext,
  type HeadersFunction,
  type MetaDescriptor,
  type MetaFunction,
  type UNSAFE_DataWithResponseInit as DataWithResponseInit,
} from 'react-router';
import { documentCacheControl, headMeta, servesDocumentHead, type DocumentData } from '@/seo/documentHead';
import type { RoutePreloadData } from '@/seo/routePreload';
import type { SeoHead } from '@/seo/templates';
import type { EntityRef, EntityType } from '@/seo/urls';

/** An entity route's loader data. Both null after an in-app navigation (the page loads it through Apollo). */
export interface EntityRouteData {
  /** The server's head for this URL. */
  head: SeoHead | null;
  /** The entity's detail-query data (`brandById`, …), as the head was built from it. */
  entity: (EntityRef & Record<string, unknown>) | null;
  /**
   * The answers of the page's secondary queries the server read (the brand
   * hub, the promotion rails), by `preloadKey` (@/seo/routePreload); absent
   * when there are none. A rail missing here loads in the browser.
   */
  preload?: RoutePreloadData;
}

export const EMPTY_ENTITY_ROUTE_DATA: EntityRouteData = Object.freeze({ head: null, entity: null });

/** The entity route loader (see the file comment). */
export async function loadEntityDocument(
  type: EntityType,
  context: AppLoadContext | undefined | null,
): Promise<EntityRouteData | DataWithResponseInit<EntityRouteData>> {
  const seo = context?.seo;
  if (!seo) return { head: null, entity: null };
  const decision = await seo.entity(type);
  if (decision.location) {
    // An entity URL only ever moves permanently (server/routes.mjs `entityPage`).
    throw redirect(decision.location, {
      status: 301,
      headers: { 'Cache-Control': documentCacheControl(301) },
    });
  }
  if (decision.status >= 400) {
    throw data(null, {
      status: decision.status,
      headers: {
        'Cache-Control': documentCacheControl(decision.status),
        ...(decision.retryAfter ? { 'Retry-After': String(decision.retryAfter) } : {}),
      },
    });
  }
  const entity = decision.entity?.item ?? null;
  // The rails belong to the served entity: never without it.
  const preload = entity && decision.preload && Object.keys(decision.preload).length ? decision.preload : null;
  return data<EntityRouteData>(
    { head: decision.head ?? null, entity, ...(preload ? { preload } : {}) },
    { status: 200, headers: { 'Cache-Control': documentCacheControl(200) } },
  );
}

/** A thrown answer's headers win (a 503's `Retry-After`), else the loader's. */
export const entityHeaders: HeadersFunction = ({ errorHeaders, loaderHeaders }) => errorHeaders ?? loaderHeaders;

/** The root route's loader data among a `meta` call's matches. */
function rootData(matches: ReadonlyArray<{ id: string; data?: unknown } | undefined>): DocumentData | undefined {
  return matches.find((match) => match?.id === 'root')?.data as DocumentData | undefined;
}

/**
 * Whether the root's answer is a 4xx head (a "{Type} not found" page): the
 * route's ErrorBoundary then takes its title and robots from `entityMeta`.
 */
export function rootNotFoundHead(root: DocumentData | null | undefined): SeoHead | null {
  return root?.head && root.error ? root.head : null;
}

/**
 * The entity route's `meta`, which replaces the root's:
 * - its own head (the document of a served entity: the root's head, built by
 *   the same decision);
 * - else the root's answer while the location is the document's URL
 *   (`servesDocumentHead`): on a 404 / 410 the root's "{Type} not found"
 *   head (`noindex`, no canonical), and the served head when the visitor
 *   comes back to that URL (the `clientLoader` brings no head);
 * - else nothing: after an in-app navigation and without the SEO server the
 *   page renders its own head (@/components/seo/PageHead, web plan W5.1), and
 *   on any other error the ErrorBoundary writes its own `<title>` and
 *   `noindex`, never the page's canonical.
 */
export const entityMeta: MetaFunction = ({ data: loaded, error, matches, location }) => {
  const own = (loaded as EntityRouteData | undefined)?.head;
  if (own) return headMeta(own);
  const root = rootData(matches);
  if (servesDocumentHead(root, location) && (!error || root.error)) return headMeta(root.head, { error: root.error });
  return [] as MetaDescriptor[];
};
